/**
 * RTK Query API slice for Scheduling Tool
 *
 * Provides API endpoints for managing schedules, classes, rooms,
 * teachers, and running the scheduling algorithm.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  Schedule,
  ScheduleFormData,
  Discipline,
  Style,
  Class,
  ClassFormData,
  Room,
  RoomFormData,
  RoomAvailability,
  RoomAvailabilityFormData,
  Teacher,
  TeacherFormData,
  TeacherAvailability,
  TeacherAvailabilityFormData,
  TeacherSpecialization,
  ClassPreference,
  ClassPreferenceFormData,
  StudioMember,
  ScheduledClass,
  RunScheduleResponse,
  SchedulePublicConfig,
  SchedulePublicConfigFormData,
  PublicScheduleResponse,
  ScheduleImport,
  ImportDraft,
  ImportIssue,
  ImportQuota,
  ImportCommitResult,
} from '../types/scheduling';

export const schedulingApi = createApi({
  reducerPath: 'schedulingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Schedules',
    'Disciplines',
    'Styles',
    'Classes',
    'Rooms',
    'RoomAvailability',
    'Teachers',
    'TeacherAvailability',
    'TeacherSpecializations',
    'ClassPreferences',
    'ScheduledClasses',
    'SchedulePublicConfigs',
    'ScheduleImports',
  ],
  endpoints: builder => ({
    // =========================================================================
    // SCHEDULES
    // =========================================================================

    /**
     * Get all schedules for a studio
     * GET /api/v1/scheduling/schedules/
     */
    getSchedules: builder.query<Schedule[], string>({
      query: studioId => ({
        url: '/scheduling/schedules/',
        params: { studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Handle both paginated and non-paginated responses
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, studioId) => [
        { type: 'Schedules', id: studioId },
        ...(result || []).map(({ id }) => ({
          type: 'Schedules' as const,
          id,
        })),
      ],
    }),

    /**
     * Get a single schedule
     * GET /api/v1/scheduling/schedules/{id}/
     * Note: Studio parameter required for permission check
     */
    getSchedule: builder.query<Schedule, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/schedules/${id}/`,
        params: { studio: studioId },
      }),
      providesTags: (result, error, { id }) => [{ type: 'Schedules', id }],
    }),

    /**
     * Create a new schedule
     * POST /api/v1/scheduling/schedules/
     */
    createSchedule: builder.mutation<
      Schedule,
      { studioId: string; data: ScheduleFormData }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/schedules/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Schedules', id: studioId },
      ],
    }),

    /**
     * Update a schedule
     * PATCH /api/v1/scheduling/schedules/{id}/\n     * Note: Studio parameter required for permission check
     */
    updateSchedule: builder.mutation<
      Schedule,
      { id: string; studioId: string; data: Partial<ScheduleFormData> }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/schedules/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Schedules', id }],
    }),

    /**
     * Delete a schedule
     * DELETE /api/v1/scheduling/schedules/{id}/
     */
    deleteSchedule: builder.mutation<void, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/schedules/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Schedules', id: studioId },
      ],
    }),

    /**
     * Duplicate a schedule with all related data
     * POST /api/v1/scheduling/schedules/{id}/duplicate/
     * Creates complete copy including classes, rooms, teachers, availability, preferences, and scheduled classes
     */
    duplicateSchedule: builder.mutation<
      Schedule & {
        summary: {
          classes_copied: number;
          rooms_copied: number;
          teachers_copied: number;
          scheduled_classes_copied: number;
          room_availability_slots_copied: number;
          teacher_availability_slots_copied: number;
          teacher_specializations_copied: number;
          class_preferences_copied: number;
        };
      },
      { id: string; studioId: string; data: { name: string } }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/schedules/${id}/duplicate/`,
        method: 'POST',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Schedules', id: studioId },
      ],
    }),

    /**
     * Run scheduling algorithm
     * POST /api/v1/scheduling/schedules/{id}/run_schedule/
     * Note: Studio parameter required for permission check
     */
    runSchedule: builder.mutation<
      RunScheduleResponse,
      { id: string; studioId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/schedules/${id}/run_schedule/`,
        method: 'POST',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Schedules', id },
        { type: 'ScheduledClasses', id },
      ],
    }),

    /**
     * Generate schedule visualization
     * POST /api/v1/scheduling/schedules/{id}/generate_visualization/
     * Supports both PNG and PDF formats via query parameter
     * Note: Uses 'output_format' parameter to avoid conflict with DRF's format suffix handling
     */
    generateVisualization: builder.mutation<
      { visualization_url: string; generated_at: string },
      { id: string; studioId?: string; format?: 'png' | 'pdf' }
    >({
      query: ({ id, format = 'png' }) => ({
        url: `/scheduling/schedules/${id}/generate_visualization/`,
        method: 'POST',
        params: { output_format: format }, // Use 'output_format' instead of 'format' to avoid DRF conflict
      }),
      // No cache invalidation needed - this just generates a file
    }),

    // =========================================================================
    // DISCIPLINES
    // =========================================================================

    /**
     * Get all disciplines for a studio
     * GET /api/v1/scheduling/disciplines/
     */
    getDisciplines: builder.query<Discipline[], string>({
      query: studioId => ({
        url: '/scheduling/disciplines/',
        params: { studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, studioId) => [
        { type: 'Disciplines', id: studioId },
        ...(result || []).map(({ id }) => ({
          type: 'Disciplines' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new discipline
     * POST /api/v1/scheduling/disciplines/
     */
    createDiscipline: builder.mutation<
      Discipline,
      { studioId: string; data: { title: string; description?: string } }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/disciplines/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Disciplines', id: studioId },
      ],
    }),

    /**
     * Update a discipline
     * PATCH /api/v1/scheduling/disciplines/{id}/
     */
    updateDiscipline: builder.mutation<
      Discipline,
      {
        id: string;
        studioId: string;
        data: Partial<{ title: string; description?: string }>;
      }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/disciplines/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id, studioId }) => [
        { type: 'Disciplines', id },
        { type: 'Disciplines', id: studioId },
        { type: 'Styles', id: studioId }, // Styles display discipline info
      ],
    }),

    /**
     * Delete a discipline
     * DELETE /api/v1/scheduling/disciplines/{id}/
     */
    deleteDiscipline: builder.mutation<void, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/disciplines/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Disciplines', id: studioId },
        { type: 'Styles', id: studioId }, // Styles may reference disciplines
      ],
    }),

    // =========================================================================
    // STYLES
    // =========================================================================

    /**
     * Get all styles for a studio
     * GET /api/v1/scheduling/styles/
     * Supports filtering by discipline title (e.g., disciplines__title__icontains=Dance)
     */
    getStyles: builder.query<
      Style[],
      { studioId: string; disciplineFilter?: string }
    >({
      query: ({ studioId, disciplineFilter }) => ({
        url: '/scheduling/styles/',
        params: {
          studio: studioId,
          page_size: 1000, // Request up to 1000 styles to avoid pagination issues
          ...(disciplineFilter
            ? { disciplines__title__icontains: disciplineFilter }
            : {}),
        },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { studioId }) => [
        { type: 'Styles', id: studioId },
        ...(result || []).map(({ id }) => ({
          type: 'Styles' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new style
     * POST /api/v1/scheduling/styles/
     */
    createStyle: builder.mutation<
      Style,
      {
        studioId: string;
        data: { title: string; description?: string; disciplines: string[] };
      }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/styles/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Styles', id: studioId },
        { type: 'Disciplines', id: studioId }, // Disciplines show style count
      ],
    }),

    /**
     * Update a style
     * PATCH /api/v1/scheduling/styles/{id}/
     */
    updateStyle: builder.mutation<
      Style,
      {
        id: string;
        studioId: string;
        data: Partial<{
          title: string;
          description?: string;
          disciplines: string[];
        }>;
      }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/styles/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id, studioId }) => [
        { type: 'Styles', id },
        { type: 'Styles', id: studioId },
        { type: 'Disciplines', id: studioId }, // Disciplines show style count
        { type: 'Classes', id: studioId }, // Classes display style info
      ],
    }),

    /**
     * Delete a style
     * DELETE /api/v1/scheduling/styles/{id}/
     */
    deleteStyle: builder.mutation<void, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/styles/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'Styles', id: studioId },
        { type: 'Disciplines', id: studioId }, // Disciplines show style count
        { type: 'Classes', id: studioId }, // Classes may reference styles
      ],
    }),

    // =========================================================================
    // CLASSES
    // =========================================================================

    /**
     * Get all classes for a schedule
     * GET /api/v1/scheduling/classes/
     * Note: Studio parameter required for permission check
     */
    getClasses: builder.query<
      Class[],
      { scheduleId: string; studioId: string }
    >({
      query: ({ scheduleId, studioId }) => ({
        url: '/scheduling/classes/',
        params: { schedule: scheduleId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Handle both paginated and non-paginated responses
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'Classes', id: scheduleId },
        ...(result || []).map(({ id }) => ({
          type: 'Classes' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new class
     * POST /api/v1/scheduling/classes/
     * Note: Studio parameter required for permission check
     */
    createClass: builder.mutation<
      Class,
      { scheduleId: string; studioId: string; data: ClassFormData }
    >({
      query: ({ scheduleId, studioId, data }) => ({
        url: '/scheduling/classes/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, schedule: scheduleId, studio: studioId },
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'Classes', id: scheduleId },
        { type: 'Schedules', id: scheduleId },
      ],
    }),

    /**
     * Update a class
     * PATCH /api/v1/scheduling/classes/{id}/
     * Note: Studio parameter required for permission check
     */
    updateClass: builder.mutation<
      Class,
      { id: string; studioId: string; data: Partial<ClassFormData> }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/classes/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Classes', id }],
    }),

    /**
     * Delete a class
     * DELETE /api/v1/scheduling/classes/{id}/
     * Note: Studio parameter required for permission check
     */
    deleteClass: builder.mutation<void, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/classes/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Classes', id }],
    }),

    // =========================================================================
    // ROOMS
    // =========================================================================

    /**
     * Get all rooms for a schedule
     * GET /api/v1/scheduling/rooms/
     * Note: Studio parameter required for permission check
     */
    getRooms: builder.query<Room[], { scheduleId: string; studioId: string }>({
      query: ({ scheduleId, studioId }) => ({
        url: '/scheduling/rooms/',
        params: { schedule: scheduleId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Handle both paginated and non-paginated responses
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'Rooms', id: scheduleId },
        ...(result || []).map(({ id }) => ({
          type: 'Rooms' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new room
     * POST /api/v1/scheduling/rooms/
     * Note: Studio parameter required for permission check
     */
    createRoom: builder.mutation<
      Room,
      { scheduleId: string; studioId: string; data: RoomFormData }
    >({
      query: ({ scheduleId, studioId, data }) => ({
        url: '/scheduling/rooms/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, schedule: scheduleId, studio: studioId },
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'Rooms', id: scheduleId },
        { type: 'Schedules', id: scheduleId },
      ],
    }),

    /**
     * Update a room
     * PATCH /api/v1/scheduling/rooms/{id}/
     * Note: Studio parameter required for permission check
     */
    updateRoom: builder.mutation<
      Room,
      { id: string; studioId: string; data: Partial<RoomFormData> }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/rooms/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Rooms', id }],
    }),

    /**
     * Delete a room
     * DELETE /api/v1/scheduling/rooms/{id}/
     * Note: Studio parameter required for permission check
     */
    deleteRoom: builder.mutation<void, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/rooms/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Rooms', id }],
    }),

    // =========================================================================
    // ROOM AVAILABILITY
    // =========================================================================

    /**
     * Get all availability slots for a room
     * GET /api/v1/scheduling/room-availability/
     */
    getRoomAvailabilities: builder.query<
      RoomAvailability[],
      { roomId: string; studioId: string }
    >({
      query: ({ roomId, studioId }) => ({
        url: '/scheduling/room-availability/',
        params: { room: roomId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { roomId }) => [
        { type: 'RoomAvailability', id: roomId },
        ...(result || []).map(({ id }) => ({
          type: 'RoomAvailability' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new room availability slot
     * POST /api/v1/scheduling/room-availability/
     */
    createRoomAvailability: builder.mutation<
      RoomAvailability,
      { studioId: string; data: RoomAvailabilityFormData }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/room-availability/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { data }) => [
        { type: 'RoomAvailability', id: data.room },
        { type: 'Rooms', id: data.room },
      ],
    }),

    /**
     * Update a room availability slot
     * PATCH /api/v1/scheduling/room-availability/{id}/
     */
    updateRoomAvailability: builder.mutation<
      RoomAvailability,
      { id: string; studioId: string; data: Partial<RoomAvailabilityFormData> }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/room-availability/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'RoomAvailability', id },
      ],
    }),

    /**
     * Delete a room availability slot
     * DELETE /api/v1/scheduling/room-availability/{id}/
     */
    deleteRoomAvailability: builder.mutation<
      void,
      { id: string; studioId: string; roomId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/room-availability/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { roomId }) => [
        { type: 'RoomAvailability', id: roomId },
        { type: 'Rooms', id: roomId },
      ],
    }),

    // =========================================================================
    // TEACHERS
    // =========================================================================

    /**
     * Get all teachers for a schedule
     * GET /api/v1/scheduling/teachers/
     * Note: Studio parameter required for permission check
     */
    getTeachers: builder.query<
      Teacher[],
      { scheduleId: string; studioId: string }
    >({
      query: ({ scheduleId, studioId }) => ({
        url: '/scheduling/teachers/',
        params: { schedule: scheduleId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'Teachers', id: scheduleId },
        ...(result || []).map(({ id }) => ({
          type: 'Teachers' as const,
          id,
        })),
      ],
    }),

    /**
     * Get studio members with teaching roles for teacher linking
     * Includes Owner, Manager, Admin, and Instructor roles
     * GET /api/v1/studio-memberships/?studio={studioId}
     */
    getStudioInstructors: builder.query<StudioMember[], string>({
      query: studioId => ({
        url: '/studio-memberships/',
        params: { studio: studioId },
      }),
      transformResponse: (response: any, meta, studioId) => {
        let members = [];
        if (Array.isArray(response)) {
          members = response;
        } else if (response && Array.isArray(response.results)) {
          members = response.results;
        }

        // Filter to only include teaching roles: owner, manager, admin, instructor
        // AND filter by the specific studio to avoid showing members from other studios
        const teachingRoles = ['owner', 'manager', 'admin', 'instructor'];
        return members.filter(
          (member: StudioMember) =>
            member.role &&
            teachingRoles.includes(member.role.toLowerCase()) &&
            member.studio === studioId
        );
      },
    }),

    /**
     * Create a new teacher
     * POST /api/v1/scheduling/teachers/
     * Note: Studio parameter required for permission check
     */
    createTeacher: builder.mutation<
      Teacher,
      { scheduleId: string; studioId: string; data: TeacherFormData }
    >({
      query: ({ scheduleId, studioId, data }) => ({
        url: '/scheduling/teachers/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, schedule: scheduleId, studio: studioId },
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'Teachers', id: scheduleId },
        { type: 'Schedules', id: scheduleId },
      ],
    }),

    /**
     * Update a teacher
     * PATCH /api/v1/scheduling/teachers/{id}/
     * Note: Studio parameter required for permission check
     */
    updateTeacher: builder.mutation<
      Teacher,
      { id: string; studioId: string; data: Partial<TeacherFormData> }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/teachers/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Teachers', id }],
    }),

    /**
     * Delete a teacher
     * DELETE /api/v1/scheduling/teachers/{id}/
     * Note: Studio parameter required for permission check
     */
    deleteTeacher: builder.mutation<void, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/teachers/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Teachers', id }],
    }),

    // =========================================================================
    // TEACHER AVAILABILITY
    // =========================================================================

    /**
     * Get all availability slots for a teacher
     * GET /api/v1/scheduling/teacher-availability/
     */
    getTeacherAvailabilities: builder.query<
      TeacherAvailability[],
      { teacherId: string; studioId: string }
    >({
      query: ({ teacherId, studioId }) => ({
        url: '/scheduling/teacher-availability/',
        params: { teacher: teacherId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { teacherId }) => [
        { type: 'TeacherAvailability', id: teacherId },
        ...(result || []).map(({ id }) => ({
          type: 'TeacherAvailability' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new teacher availability slot
     * POST /api/v1/scheduling/teacher-availability/
     */
    createTeacherAvailability: builder.mutation<
      TeacherAvailability,
      { studioId: string; data: TeacherAvailabilityFormData }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/teacher-availability/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { data }) => [
        { type: 'TeacherAvailability', id: data.teacher },
        { type: 'Teachers', id: data.teacher },
      ],
    }),

    /**
     * Update a teacher availability slot
     * PATCH /api/v1/scheduling/teacher-availability/{id}/
     */
    updateTeacherAvailability: builder.mutation<
      TeacherAvailability,
      {
        id: string;
        studioId: string;
        data: Partial<TeacherAvailabilityFormData>;
      }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/teacher-availability/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TeacherAvailability', id },
      ],
    }),

    deleteTeacherAvailability: builder.mutation<
      void,
      { id: string; studioId: string; teacherId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/teacher-availability/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { teacherId }) => [
        { type: 'TeacherAvailability', id: teacherId },
        { type: 'Teachers', id: teacherId },
      ],
    }),

    // =========================================================================
    // TEACHER SPECIALIZATIONS
    // =========================================================================

    /**
     * Get all specializations for a teacher
     * GET /api/v1/scheduling/teacher-specializations/
     */
    getTeacherSpecializations: builder.query<
      TeacherSpecialization[],
      { teacherId: string; studioId: string }
    >({
      query: ({ teacherId, studioId }) => ({
        url: '/scheduling/teacher-specializations/',
        params: { teacher: teacherId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { teacherId }) => [
        { type: 'TeacherSpecializations', id: teacherId },
        ...(result || []).map(({ id }) => ({
          type: 'TeacherSpecializations' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new teacher specialization
     * POST /api/v1/scheduling/teacher-specializations/
     */
    createTeacherSpecialization: builder.mutation<
      TeacherSpecialization,
      {
        studioId: string;
        data: {
          teacher: string;
          specialization_type: 'style' | 'age_group';
          specialization_value: string;
        };
      }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/teacher-specializations/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { data }) => [
        { type: 'TeacherSpecializations', id: data.teacher },
        { type: 'Teachers', id: data.teacher },
      ],
    }),

    /**
     * Delete a teacher specialization
     * DELETE /api/v1/scheduling/teacher-specializations/{id}/
     */
    deleteTeacherSpecialization: builder.mutation<
      void,
      { id: string; studioId: string; teacherId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/teacher-specializations/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { teacherId }) => [
        { type: 'TeacherSpecializations', id: teacherId },
        { type: 'Teachers', id: teacherId },
      ],
    }),

    // =========================================================================
    // CLASS PREFERENCES
    // =========================================================================

    /**
     * Get all preferences for a class
     * GET /api/v1/scheduling/class-preferences/
     */
    getClassPreferences: builder.query<
      ClassPreference[],
      { classId: string; studioId: string }
    >({
      query: ({ classId, studioId }) => ({
        url: '/scheduling/class-preferences/',
        params: { class_obj: classId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { classId }) => [
        { type: 'ClassPreferences', id: classId },
        ...(result || []).map(({ id }) => ({
          type: 'ClassPreferences' as const,
          id,
        })),
      ],
    }),

    /**
     * Create a new class preference
     * POST /api/v1/scheduling/class-preferences/
     */
    createClassPreference: builder.mutation<
      ClassPreference,
      { studioId: string; data: ClassPreferenceFormData }
    >({
      query: ({ studioId, data }) => ({
        url: '/scheduling/class-preferences/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, studio: studioId },
      }),
      invalidatesTags: (result, error, { data }) => [
        { type: 'ClassPreferences', id: data.class_obj },
        { type: 'Classes', id: data.class_obj },
      ],
    }),

    /**
     * Update a class preference
     * PATCH /api/v1/scheduling/class-preferences/{id}/
     */
    updateClassPreference: builder.mutation<
      ClassPreference,
      {
        id: string;
        studioId: string;
        data: Partial<ClassPreferenceFormData>;
      }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/class-preferences/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ClassPreferences', id },
      ],
    }),

    /**
     * Delete a class preference
     * DELETE /api/v1/scheduling/class-preferences/{id}/
     */
    deleteClassPreference: builder.mutation<
      void,
      { id: string; studioId: string; classId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/class-preferences/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { classId }) => [
        { type: 'ClassPreferences', id: classId },
        { type: 'Classes', id: classId },
      ],
    }),

    // =========================================================================
    // SCHEDULED CLASSES
    // =========================================================================

    /**
     * Get all scheduled classes for a schedule
     * GET /api/v1/scheduling/scheduled-classes/
     * Note: Studio parameter required for permission check
     */
    getScheduledClasses: builder.query<
      ScheduledClass[],
      { scheduleId: string; studioId: string }
    >({
      query: ({ scheduleId, studioId }) => ({
        url: '/scheduling/scheduled-classes/',
        params: { schedule: scheduleId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Handle both paginated and non-paginated responses
        if (Array.isArray(response)) {
          return response;
        }
        if (response && Array.isArray(response.results)) {
          return response.results;
        }
        return [];
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'ScheduledClasses', id: scheduleId },
      ],
    }),

    /**
     * Create a new scheduled class
     * POST /api/v1/scheduling/scheduled-classes/
     */
    createScheduledClass: builder.mutation<
      ScheduledClass,
      {
        scheduleId: string;
        studioId: string;
        data: {
          class_obj: string;
          room: string;
          teachers?: string[]; // Array of teacher IDs (ManyToMany)
          day: string;
          start_time: string;
          end_time: string;
          is_manually_scheduled?: boolean; // True if manually created, false if algorithm-generated
        };
      }
    >({
      query: ({ scheduleId, studioId, data }) => ({
        url: '/scheduling/scheduled-classes/',
        method: 'POST',
        params: { studio: studioId },
        body: { ...data, schedule: scheduleId, studio: studioId },
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'ScheduledClasses', id: scheduleId },
      ],
    }),

    /**
     * Update a scheduled class
     * PATCH /api/v1/scheduling/scheduled-classes/{id}/
     */
    updateScheduledClass: builder.mutation<
      ScheduledClass,
      {
        id: string;
        studioId: string;
        scheduleId: string; // Need scheduleId to invalidate the correct cache
        data: Partial<{
          class_obj: string;
          room: string;
          teachers: string[]; // Array of teacher IDs (ManyToMany)
          day: string;
          start_time: string;
          end_time: string;
          is_manually_scheduled: boolean; // True if manually created/edited, false if algorithm-generated
        }>;
      }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/scheduled-classes/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'ScheduledClasses', id: scheduleId },
      ],
    }),

    /**
     * Delete a scheduled class
     * DELETE /api/v1/scheduling/scheduled-classes/{id}/
     */
    deleteScheduledClass: builder.mutation<
      void,
      { id: string; studioId: string; scheduleId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/scheduled-classes/${id}/`,
        method: 'DELETE',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'ScheduledClasses', id: scheduleId },
      ],
    }),

    // =========================================================================
    // SCHEDULE PUBLIC CONFIGURATION
    // =========================================================================

    /**
     * Get schedule public page configuration (authenticated)
     * GET /api/v1/scheduling/schedule-public-configs/
     * Auto-creates config if it doesn't exist
     */
    getSchedulePublicConfig: builder.query<
      SchedulePublicConfig,
      { scheduleId: string; studioId: string }
    >({
      query: ({ scheduleId, studioId }) => ({
        url: '/scheduling/schedule-public-configs/',
        params: { schedule: scheduleId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        // Backend returns array, extract first item (auto-created if doesn't exist)
        if (Array.isArray(response)) {
          return response[0];
        }
        if (response && Array.isArray(response.results)) {
          return response.results[0];
        }
        return response;
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'SchedulePublicConfigs', id: scheduleId },
      ],
    }),

    /**
     * Update schedule public page configuration (authenticated)
     * PATCH /api/v1/scheduling/schedule-public-configs/{id}/
     */
    updateSchedulePublicConfig: builder.mutation<
      SchedulePublicConfig,
      {
        id: string;
        studioId: string;
        scheduleId: string;
        data: Partial<SchedulePublicConfigFormData>;
      }
    >({
      query: ({ id, studioId, data }) => ({
        url: `/scheduling/schedule-public-configs/${id}/`,
        method: 'PATCH',
        params: { studio: studioId },
        body: data,
      }),
      invalidatesTags: (result, error, { scheduleId }) => [
        { type: 'SchedulePublicConfigs', id: scheduleId },
      ],
    }),

    /**
     * Get public schedule display (no authentication required)
     * GET /api/v1/public-schedule/{scheduleIdOrSlug}/
     * Supports both UUID and studio slug
     */
    getPublicSchedule: builder.query<PublicScheduleResponse, string>({
      query: scheduleIdOrSlug => ({
        url: `/public-schedule/${scheduleIdOrSlug}/`,
      }),
      // No auth required, no tags needed (public data)
    }),

    // =========================================================================
    // SCHEDULE IMPORT (structured / AI import wizard)
    // =========================================================================

    /** List imports for a schedule. GET /scheduling/imports/?schedule=&studio= */
    getImports: builder.query<
      ScheduleImport[],
      { scheduleId: string; studioId: string }
    >({
      query: ({ scheduleId, studioId }) => ({
        url: '/scheduling/imports/',
        params: { schedule: scheduleId, studio: studioId },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response;
        if (response && Array.isArray(response.results))
          return response.results;
        return [];
      },
      providesTags: (result, error, { scheduleId }) => [
        { type: 'ScheduleImports', id: scheduleId },
        ...(result || []).map(({ id }) => ({
          type: 'ScheduleImports' as const,
          id,
        })),
      ],
    }),

    /** Get one import (status/draft). GET /scheduling/imports/{id}/?studio= */
    getImport: builder.query<ScheduleImport, { id: string; studioId: string }>({
      query: ({ id, studioId }) => ({
        url: `/scheduling/imports/${id}/`,
        params: { studio: studioId },
      }),
      providesTags: (result, error, { id }) => [
        { type: 'ScheduleImports', id },
      ],
    }),

    /** Monthly AI-import quota. GET /scheduling/imports/quota/?studio= */
    getImportQuota: builder.query<ImportQuota, { studioId: string }>({
      query: ({ studioId }) => ({
        url: '/scheduling/imports/quota/',
        params: { studio: studioId },
      }),
      providesTags: (result, error, { studioId }) => [
        { type: 'ScheduleImports', id: `quota-${studioId}` },
      ],
    }),

    /**
     * Upload a file (or paste text) to start an import.
     * POST /scheduling/imports/?studio=  (multipart)
     */
    createImportUpload: builder.mutation<
      ScheduleImport,
      { scheduleId: string; studioId: string; file?: File; text?: string }
    >({
      query: ({ scheduleId, studioId, file, text }) => {
        const formData = new FormData();
        formData.append('schedule', scheduleId);
        if (file) formData.append('file', file);
        if (text) formData.append('text', text);
        return {
          url: '/scheduling/imports/',
          method: 'POST',
          params: { studio: studioId },
          body: formData,
        };
      },
      invalidatesTags: (result, error, { scheduleId, studioId }) => [
        { type: 'ScheduleImports', id: scheduleId },
        { type: 'ScheduleImports', id: `quota-${studioId}` },
      ],
    }),

    /** Persist reviewer edits. POST /scheduling/imports/{id}/draft/?studio= */
    saveImportDraft: builder.mutation<
      { draft: ImportDraft; issues: ImportIssue[] },
      { id: string; studioId: string; draft: ImportDraft }
    >({
      query: ({ id, studioId, draft }) => ({
        url: `/scheduling/imports/${id}/draft/`,
        method: 'POST',
        params: { studio: studioId },
        body: { draft },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ScheduleImports', id },
      ],
    }),

    /**
     * Commit the draft to the schedule.
     * POST /scheduling/imports/{id}/commit/?studio=
     */
    commitImport: builder.mutation<
      ImportCommitResult,
      {
        id: string;
        studioId: string;
        scheduleId: string;
        mode: 'add' | 'replace';
        confirm?: string;
        forceAll?: boolean;
      }
    >({
      query: ({ id, studioId, mode, confirm, forceAll }) => ({
        url: `/scheduling/imports/${id}/commit/`,
        method: 'POST',
        params: { studio: studioId },
        body: { mode, confirm, force_all: forceAll },
      }),
      invalidatesTags: (result, error, { id, scheduleId }) => [
        { type: 'ScheduleImports', id },
        { type: 'ScheduleImports', id: scheduleId },
        { type: 'ScheduledClasses', id: scheduleId },
        { type: 'Classes', id: scheduleId },
        { type: 'Rooms', id: scheduleId },
        { type: 'Teachers', id: scheduleId },
        { type: 'Schedules', id: scheduleId },
      ],
    }),

    /** Undo a committed import. POST /scheduling/imports/{id}/undo/?studio= */
    undoImport: builder.mutation<
      { status: string; restored_placements: number },
      { id: string; studioId: string; scheduleId: string }
    >({
      query: ({ id, studioId }) => ({
        url: `/scheduling/imports/${id}/undo/`,
        method: 'POST',
        params: { studio: studioId },
      }),
      invalidatesTags: (result, error, { id, scheduleId }) => [
        { type: 'ScheduleImports', id },
        { type: 'ScheduleImports', id: scheduleId },
        { type: 'ScheduledClasses', id: scheduleId },
        { type: 'Classes', id: scheduleId },
        { type: 'Rooms', id: scheduleId },
        { type: 'Teachers', id: scheduleId },
        { type: 'Schedules', id: scheduleId },
      ],
    }),
  }),
});

export const {
  useGetSchedulesQuery,
  useGetScheduleQuery,
  useCreateScheduleMutation,
  useUpdateScheduleMutation,
  useDeleteScheduleMutation,
  useDuplicateScheduleMutation,
  useRunScheduleMutation,
  useGenerateVisualizationMutation,
  useGetDisciplinesQuery,
  useCreateDisciplineMutation,
  useUpdateDisciplineMutation,
  useDeleteDisciplineMutation,
  useGetStylesQuery,
  useCreateStyleMutation,
  useUpdateStyleMutation,
  useDeleteStyleMutation,
  useGetClassesQuery,
  useCreateClassMutation,
  useUpdateClassMutation,
  useDeleteClassMutation,
  useGetRoomsQuery,
  useCreateRoomMutation,
  useUpdateRoomMutation,
  useDeleteRoomMutation,
  useGetRoomAvailabilitiesQuery,
  useCreateRoomAvailabilityMutation,
  useUpdateRoomAvailabilityMutation,
  useDeleteRoomAvailabilityMutation,
  useGetTeachersQuery,
  useGetStudioInstructorsQuery,
  useCreateTeacherMutation,
  useUpdateTeacherMutation,
  useDeleteTeacherMutation,
  useGetTeacherAvailabilitiesQuery,
  useCreateTeacherAvailabilityMutation,
  useUpdateTeacherAvailabilityMutation,
  useDeleteTeacherAvailabilityMutation,
  useGetTeacherSpecializationsQuery,
  useCreateTeacherSpecializationMutation,
  useDeleteTeacherSpecializationMutation,
  useGetClassPreferencesQuery,
  useCreateClassPreferenceMutation,
  useUpdateClassPreferenceMutation,
  useDeleteClassPreferenceMutation,
  useGetScheduledClassesQuery,
  useCreateScheduledClassMutation,
  useUpdateScheduledClassMutation,
  useDeleteScheduledClassMutation,
  useGetSchedulePublicConfigQuery,
  useUpdateSchedulePublicConfigMutation,
  useGetPublicScheduleQuery,
  useGetImportsQuery,
  useGetImportQuery,
  useGetImportQuotaQuery,
  useCreateImportUploadMutation,
  useSaveImportDraftMutation,
  useCommitImportMutation,
  useUndoImportMutation,
} = schedulingApi;
